import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ── Toast ──
const ToastCtx = createContext<(msg: string, err?: boolean) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const show = useCallback((msg: string, err = false) => {
    setToast({ msg, err });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {toast && <div className={'toast' + (toast.err ? ' err' : '')}>{toast.msg}</div>}
    </ToastCtx.Provider>
  );
}

// ── Modal ──
export function Modal({ title, onClose, children, large }: { title: string; onClose: () => void; children: React.ReactNode; large?: boolean }) {
  // Esc bilan yopish + orqa fon skroll qilinmasin
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="overlay" onClick={onClose}>
      <div className={'modal' + (large ? ' modal-lg' : '')} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>{title}</h3>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Field helpers ──
export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
    </div>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={'input ' + (props.className || '')} />;
}

/** Yuklanish — bo'sh matn o'rniga jadval shakliga yaqin skelet. */
export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton" aria-busy="true" aria-label="Yuklanmoqda">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="sk-row" style={{ opacity: 1 - i * 0.13 }} />
      ))}
    </div>
  );
}
export function Empty({ text, hint }: { text: string; hint?: string }) {
  return (
    <div className="empty">
      <div className="e-title">{text}</div>
      {hint && <div>{hint}</div>}
    </div>
  );
}

/** Rang harorati nuqtasi — matnda 3000K/4000K/6500K bo'lsa haqiqiy rangda ko'rsatadi. */
export function KelvinDot({ text }: { text?: string | null }) {
  const m = String(text || '').match(/\b(3000|4000|6500)\s*K\b/i);
  if (!m) return null;
  return <span className={`k-dot k-${m[1]}`} title={`${m[1]}K rang harorati`} />;
}

/** Artikul, TNVED, hujjat raqami — monoshirina bilan, solishtirish oson bo'lsin. */
export function Code({ children }: { children: React.ReactNode }) {
  return <span className="code">{children}</span>;
}

/** Jadval qatoriga bosish. Matn belgilangan bo'lsa hujjat OCHILMAYDI —
 *  qatordan nusxa olayotganda ichkariga kirib ketmasligi uchun. */
export function rowNav(handler: () => void) {
  return (e: React.MouseEvent) => {
    if ((e.target as HTMLElement)?.closest('button, a, input, select, textarea, label')) return;
    const sel = window.getSelection?.();
    if (sel && !sel.isCollapsed && sel.toString().trim().length > 0) return;
    handler();
  };
}

/** So'rov xato bersa — «Yuklanmoqda…» da qotib qolmasin. */
export function ErrorBox({ text, onRetry }: { text: string; onRetry?: () => void }) {
  return (
    <div className="empty">
      <div className="e-title">Ma'lumotni yuklab bo'lmadi</div>
      <div style={{ marginBottom: 14 }}>{text}</div>
      {onRetry && <button className="btn" onClick={onRetry}>Qayta urinish</button>}
    </div>
  );
}

/** Bo'lim sarlavhasi — teal chiziq + UPPERCASE nom + muted izoh. */
export function Eyebrow({ title, desc }: { title: string; desc?: string }) {
  return (
    <div className="eyebrow">
      <b>{title}</b>
      {desc && <span>{desc}</span>}
    </div>
  );
}
